/**
 * ReferenceImagePanel — the "Use a reference image" capability (issue #136),
 * shared by every creative surface (issue #140).
 *
 * Three real, authenticated modes on the proven ComfyUI/FLUX.2 image pipeline:
 *   • Inspire   → POST /api/creative/image/generate/inspire
 *   • Edit      → POST /api/creative/image/generate/edit   (client-built mask)
 *   • Composite → POST /api/creative/image/composite-asset
 * The finished creative renders through its authenticated asset URL. Generating
 * records a PREVIEW the operator Keeps to register a creative (issue #149) — it
 * NEVER activates an ad or authorizes spend.
 *
 * This is the single implementation. CreativeWorkspace's image lane and the
 * Image Ad Studio both render <ReferenceImagePanel offerId=… />; the logic is
 * not forked. Styling is self-contained (inline) so it reads the same on both
 * dark surfaces. It takes only `offerId` and uses the same cookie session
 * (withCredentials) as every other call in those surfaces.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ax = { withCredentials: true };
const readErr = (e) => e?.response?.data?.detail || e?.message || "Request failed";

/** Reference-image modes (issue #136) — Inspire / Composite / Edit, each wired
 *  to its real multipart endpoint on the proven ComfyUI/FLUX.2 pipeline. The
 *  operator brings their own image and NEVER cuts anything out; for Edit the
 *  mask PNG (white = change, black = keep) is produced client-side from a marked
 *  region. Every call is authenticated through the same cookie session as the
 *  rest of the workspace. Generating records a preview (kept to register a
 *  creative, issue #149) — it never activates
 *  an ad or authorizes spend. */
const REF_MODES = [
  { id: "inspire", label: "Inspire", blurb: "Generate a brand-new scene guided by a reference image you upload." },
  { id: "composite", label: "Composite", blurb: "Place an uploaded asset (logo, element, or whole image) onto a base — the system handles cleanliness." },
  { id: "edit", label: "Edit", blurb: "Keep a base image and regenerate only the region you mark." },
];

export default function ReferenceImagePanel({ offerId }) {
  const [mode, setMode] = useState("inspire");
  const active = REF_MODES.find((m) => m.id === mode);
  return (
    <Card title="Use a reference image">
      <p style={sub}>Bring your own image into the pipeline. Pick a mode — each does something different. Generating records a preview you can Keep to register a creative; it never activates an ad or authorizes spend.</p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        {REF_MODES.map((m) => (
          <button key={m.id} data-testid={`ref-mode-${m.id}`}
            style={{ ...tab, ...(m.id === mode ? tabActive : {}) }} onClick={() => setMode(m.id)}>{m.label}</button>
        ))}
      </div>
      <p style={sub}>{active.blurb}</p>
      {mode === "inspire" && <InspireForm offerId={offerId} />}
      {mode === "composite" && <CompositeForm offerId={offerId} />}
      {mode === "edit" && <EditForm offerId={offerId} />}
    </Card>
  );
}

/** Object URL for a picked File, revoked on change/unmount. */
function usePreviewUrl(file) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file) { setUrl(null); return undefined; }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}

/** The returned PREVIEW (issue #149), shown inside Cipher via its authenticated
 *  asset URL. Generation now records a preview, NOT a tracked creative — the
 *  operator explicitly KEEPs it to register the attributable creative (mirrors
 *  the Image Ad Studio review Submit). Keeping only records the creative; it
 *  never activates an ad or authorizes spend. */
function RefResult({ result }) {
  const [kept, setKept] = useState(null);
  const [keeping, setKeeping] = useState(false);
  const [keepErr, setKeepErr] = useState(null);
  // A new result clears any prior keep state.
  const pid = result && (result.preview_id || result.creative_id);
  useEffect(() => { setKept(null); setKeeping(false); setKeepErr(null); }, [pid]);
  if (!result) return null;
  const src = result.asset_url
    ? `${BACKEND_URL}${result.asset_url}`
    : `${API}/creative/image/creatives/${pid}/asset`;
  const modeLabel = result.generation_mode || result.mode;

  const keep = async () => {
    setKeeping(true); setKeepErr(null);
    try {
      const r = await axios.post(
        `${API}/creative/image/previews/${pid}/keep`, {}, ax);
      setKept(r.data);
    } catch (e) {
      setKeepErr(readErr(e));
    } finally {
      setKeeping(false);
    }
  };

  return (
    <div style={{ marginTop: 10 }}>
      <p style={sub}>Preview <code>{pid}</code>{modeLabel ? <> · {modeLabel}</> : null}. This is a preview, not a tracked creative — Keep it to register it.</p>
      <img src={src} alt="Generated preview" style={resultImg} />
      {kept ? (
        <p style={sub} data-testid="ref-kept">
          Kept as creative <code>{kept.creative_name || kept.creative_id}</code>. Saved draft, not an activated ad.
        </p>
      ) : (
        <div style={{ marginTop: 8 }}>
          <button style={ghostBtn} data-testid="ref-keep" disabled={keeping || !pid} onClick={keep}>
            {keeping ? "Keeping…" : "Keep this creative"}
          </button>
          {keepErr ? <p style={{ ...sub, color: "#ff9a9a" }} data-testid="ref-keep-error">{keepErr}</p> : null}
        </div>
      )}
    </div>
  );
}

function FileField({ label, file, onPick, testid }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <label style={fieldLabel}>{label}</label>
      <input type="file" accept="image/*" data-testid={testid} style={{ color: "#dfe8ff", fontSize: 13 }}
        onChange={(e) => onPick(e.target.files?.[0] || null)} />
    </div>
  );
}

/** Numeric w / h / seed row shared by Inspire and Edit. */
function DimsRow({ w, h, seed, setW, setH, setSeed }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
      <input style={input} inputMode="numeric" placeholder="Width" value={w} onChange={(e) => setW(e.target.value)} />
      <input style={input} inputMode="numeric" placeholder="Height" value={h} onChange={(e) => setH(e.target.value)} />
      <input style={input} inputMode="numeric" placeholder="Seed (optional)" value={seed} onChange={(e) => setSeed(e.target.value)} />
    </div>
  );
}

/** INSPIRE — reference image + prompt → a new scene. */
function InspireForm({ offerId }) {
  const [reference, setReference] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [w, setW] = useState("1080");
  const [h, setH] = useState("1080");
  const [seed, setSeed] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const preview = usePreviewUrl(reference);

  async function submit() {
    if (!reference || !prompt.trim()) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append("offer_id", offerId);
      fd.append("prompt", prompt.trim());
      fd.append("reference", reference);
      fd.append("w", String(parseInt(w, 10) || 1080));
      fd.append("h", String(parseInt(h, 10) || 1080));
      if (String(seed).trim()) fd.append("seed", String(seed).trim());
      const r = await axios.post(`${API}/creative/image/generate/inspire`, fd, ax);
      setResult(r.data);
    } catch (e) { setError(readErr(e)); } finally { setBusy(false); }
  }

  return (
    <div>
      <FileField label="Reference image" file={reference} onPick={setReference} testid="ref-inspire-file" />
      {preview && <img src={preview} alt="Reference" style={previewImg} />}
      <label style={fieldLabel}>New scene prompt</label>
      <textarea style={ta} data-testid="ref-inspire-prompt" placeholder="Describe the new scene the reference should inspire…"
        value={prompt} onChange={(e) => setPrompt(e.target.value)} />
      <DimsRow w={w} h={h} seed={seed} setW={setW} setH={setH} setSeed={setSeed} />
      <button style={ghostBtn} data-testid="ref-inspire-submit" disabled={busy || !reference || !prompt.trim()} onClick={submit}>
        {busy ? "Generating…" : "Generate from reference"}
      </button>
      {error && <p style={errText}>{error}</p>}
      <RefResult result={result} />
    </div>
  );
}

/** EDIT / INPAINT — base + a marked region → regenerate only that region. The
 *  mask is drawn client-side: black everywhere, white over the marked box. */
function EditForm({ offerId }) {
  const [base, setBase] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [w, setW] = useState("1080");
  const [h, setH] = useState("1080");
  const [seed, setSeed] = useState("");
  const [rect, setRect] = useState(null); // fractional {x,y,w,h}
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const preview = usePreviewUrl(base);
  const overlayRef = useRef(null);
  const drag = useRef(null);

  // When the base loads, default output dims to its natural size so the mask
  // maps 1:1 onto what the operator sees.
  function onImgLoad(e) {
    const nw = e.target.naturalWidth, nh = e.target.naturalHeight;
    if (nw && nh) { setW(String(nw)); setH(String(nh)); }
  }

  function frac(e) {
    const el = overlayRef.current; if (!el) return null;
    const b = el.getBoundingClientRect();
    return {
      fx: Math.min(1, Math.max(0, (e.clientX - b.left) / b.width)),
      fy: Math.min(1, Math.max(0, (e.clientY - b.top) / b.height)),
    };
  }
  function down(e) { const p = frac(e); if (!p) return; drag.current = p; setRect({ x: p.fx, y: p.fy, w: 0, h: 0 }); e.preventDefault(); }
  function move(e) {
    if (!drag.current) return;
    const p = frac(e); if (!p) return; const s = drag.current;
    setRect({ x: Math.min(s.fx, p.fx), y: Math.min(s.fy, p.fy), w: Math.abs(p.fx - s.fx), h: Math.abs(p.fy - s.fy) });
  }
  function up() { drag.current = null; }

  async function buildMask(ow, oh) {
    const c = document.createElement("canvas");
    c.width = ow; c.height = oh;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#000"; ctx.fillRect(0, 0, ow, oh);
    if (rect && rect.w > 0 && rect.h > 0) {
      ctx.fillStyle = "#fff";
      ctx.fillRect(Math.round(rect.x * ow), Math.round(rect.y * oh), Math.round(rect.w * ow), Math.round(rect.h * oh));
    }
    return await new Promise((res) => c.toBlob((b) => res(b), "image/png"));
  }

  const haveRegion = rect && rect.w > 0.01 && rect.h > 0.01;

  async function submit() {
    if (!base || !prompt.trim() || !haveRegion) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const ow = parseInt(w, 10) || 1080, oh = parseInt(h, 10) || 1080;
      const maskBlob = await buildMask(ow, oh);
      const fd = new FormData();
      fd.append("offer_id", offerId);
      fd.append("prompt", prompt.trim());
      fd.append("base", base);
      fd.append("mask", new File([maskBlob], "mask.png", { type: "image/png" }));
      fd.append("w", String(ow));
      fd.append("h", String(oh));
      if (String(seed).trim()) fd.append("seed", String(seed).trim());
      const r = await axios.post(`${API}/creative/image/generate/edit`, fd, ax);
      setResult(r.data);
    } catch (e) { setError(readErr(e)); } finally { setBusy(false); }
  }

  return (
    <div>
      <FileField label="Base image" file={base} onPick={(f) => { setBase(f); setRect(null); }} testid="ref-edit-file" />
      {preview && (
        <>
          <p style={sub}>Drag over the base to mark the region to change. White in the mask = regenerate, black = keep.</p>
          <div style={stageBox}>
            <img src={preview} alt="Base" style={stageImg} onLoad={onImgLoad} draggable={false} />
            <div ref={overlayRef} style={stageOverlay}
              onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}>
              {rect && (
                <div style={{
                  position: "absolute",
                  left: `${rect.x * 100}%`, top: `${rect.y * 100}%`,
                  width: `${rect.w * 100}%`, height: `${rect.h * 100}%`,
                  background: "rgba(255,255,255,0.35)", border: "2px solid #2f6df6",
                }} />
              )}
            </div>
          </div>
        </>
      )}
      <label style={fieldLabel}>What goes in the marked region</label>
      <textarea style={ta} data-testid="ref-edit-prompt" placeholder="Describe what should replace the marked region…"
        value={prompt} onChange={(e) => setPrompt(e.target.value)} />
      <DimsRow w={w} h={h} seed={seed} setW={setW} setH={setH} setSeed={setSeed} />
      <button style={ghostBtn} data-testid="ref-edit-submit" disabled={busy || !base || !prompt.trim() || !haveRegion} onClick={submit}>
        {busy ? "Generating…" : "Regenerate marked region"}
      </button>
      {!haveRegion && base && <p style={sub}>Mark a region on the base to enable Generate.</p>}
      {error && <p style={errText}>{error}</p>}
      <RefResult result={result} />
    </div>
  );
}

/** COMPOSITE — upload an asset and place it on a base; the operator never cuts
 *  anything out. "Use the entire image" is an explicit, stated choice. */
const COMPOSITE_MODES = [
  { id: "logo-auto-clean", label: "Auto-clean a logo", blurb: "Removes the background automatically so a logo drops in with no white box." },
  { id: "element-exact", label: "Place an element exactly", blurb: "Layers the element as given, keeping its own transparency — no background removal." },
  { id: "use-entire-image", label: "Use the entire image", blurb: "Composites the WHOLE uploaded image unchanged, background and all — stated explicitly." },
];

function CompositeForm({ offerId }) {
  const [asset, setAsset] = useState(null);
  const [base, setBase] = useState(null);
  const [mode, setMode] = useState("logo-auto-clean");
  const [x, setX] = useState(0.5);
  const [y, setY] = useState(0.5);
  const [scale, setScale] = useState(0.35);
  const [tolerance, setTolerance] = useState(30);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const assetPreview = usePreviewUrl(asset);
  const basePreview = usePreviewUrl(base);
  const overlayRef = useRef(null);
  const dragging = useRef(false);

  function frac(e) {
    const el = overlayRef.current; if (!el) return null;
    const b = el.getBoundingClientRect();
    return {
      fx: Math.min(1, Math.max(0, (e.clientX - b.left) / b.width)),
      fy: Math.min(1, Math.max(0, (e.clientY - b.top) / b.height)),
    };
  }
  function place(e) { const p = frac(e); if (!p) return; setX(p.fx); setY(p.fy); }
  function down(e) { dragging.current = true; place(e); e.preventDefault(); }
  function move(e) { if (dragging.current) place(e); }
  function up() { dragging.current = false; }

  const whole = mode === "use-entire-image";

  async function submit() {
    if (!asset || !base) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append("asset", asset);
      fd.append("base", base);
      fd.append("mode", mode);
      fd.append("x", String(x));
      fd.append("y", String(y));
      if (!whole) fd.append("scale", String(scale));
      fd.append("tolerance", String(tolerance));
      if (offerId) fd.append("offer_id", offerId);
      const r = await axios.post(`${API}/creative/image/composite-asset`, fd, ax);
      setResult(r.data);
    } catch (e) { setError(readErr(e)); } finally { setBusy(false); }
  }

  return (
    <div>
      <FileField label="Asset to place (logo / element / image)" file={asset} onPick={setAsset} testid="ref-composite-asset" />
      <FileField label="Base image" file={base} onPick={setBase} testid="ref-composite-base" />

      <label style={fieldLabel}>How should the asset be handled?</label>
      {COMPOSITE_MODES.map((m) => (
        <label key={m.id} style={{ ...reviewRow, alignItems: "flex-start" }}>
          <input type="radio" name="composite-mode" data-testid={`ref-composite-mode-${m.id}`}
            checked={mode === m.id} onChange={() => setMode(m.id)} />
          <span><strong style={{ color: "#9db4e0" }}>{m.label}</strong> — <span style={sub}>{m.blurb}</span></span>
        </label>
      ))}

      {basePreview ? (
        <>
          <p style={sub}>{whole ? "The whole image is used; drag only sets where its centre lands." : "Drag on the base to set where the asset's centre lands."}</p>
          <div style={stageBox}>
            <img src={basePreview} alt="Base" style={stageImg} draggable={false} />
            <div ref={overlayRef} style={stageOverlay}
              onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}>
              <div style={{
                position: "absolute",
                left: `${x * 100}%`, top: `${y * 100}%`,
                width: `${(whole ? 1 : scale) * 100}%`,
                transform: "translate(-50%,-50%)",
                border: "2px solid #2f6df6", borderRadius: 4,
                background: assetPreview ? "transparent" : "rgba(47,109,246,0.25)",
                pointerEvents: "none",
              }}>
                {assetPreview && <img src={assetPreview} alt="Asset" style={{ width: "100%", display: "block", opacity: 0.9 }} />}
              </div>
            </div>
          </div>
        </>
      ) : <p style={sub}>Upload a base image to position the asset.</p>}

      {!whole && (
        <div style={{ marginBottom: 8 }}>
          <label style={fieldLabel}>Scale — asset width as a fraction of the base ({Math.round(scale * 100)}%)</label>
          <input type="range" min="0.05" max="1" step="0.01" value={scale} data-testid="ref-composite-scale"
            onChange={(e) => setScale(parseFloat(e.target.value))} style={{ width: "100%" }} />
        </div>
      )}
      {mode === "logo-auto-clean" && (
        <div style={{ marginBottom: 8 }}>
          <label style={fieldLabel}>Background-cleanup tolerance ({tolerance})</label>
          <input type="range" min="0" max="80" step="1" value={tolerance}
            onChange={(e) => setTolerance(parseInt(e.target.value, 10))} style={{ width: "100%" }} />
        </div>
      )}

      <button style={ghostBtn} data-testid="ref-composite-submit" disabled={busy || !asset || !base} onClick={submit}>
        {busy ? "Compositing…" : "Composite onto base"}
      </button>
      {error && <p style={errText}>{error}</p>}
      <RefResult result={result} />
    </div>
  );
}

// ---- self-contained presentational helpers (no external design import) ----
function Card({ title, children }) {
  return (<div style={card}><div style={cardTitle}>{title}</div>{children}</div>);
}

const card = { border: "1px solid #2a3550", borderRadius: 10, padding: 14, marginBottom: 12, background: "rgba(20,28,48,0.5)" };
const cardTitle = { fontWeight: 600, marginBottom: 8 };
const sub = { color: "#8aa0c8", fontSize: 13 };
const ghostBtn = { padding: "8px 14px", border: "1px solid #3a4a6a", borderRadius: 8, background: "#1d2740", color: "#dfe8ff", cursor: "pointer" };
const tab = { padding: "6px 10px", border: "1px solid #2a3550", borderRadius: 999, background: "#18213a", color: "#bcd", cursor: "pointer" };
const tabActive = { background: "#2f6df6", color: "#fff", borderColor: "#2f6df6" };
const ta = { width: "100%", minHeight: 60, background: "#0f1830", color: "#dfe8ff", border: "1px solid #2a3550", borderRadius: 8, padding: 8, marginBottom: 8 };
const fieldLabel = { display: "block", color: "#9db4e0", fontSize: 12, fontWeight: 600, margin: "6px 0 4px" };
const input = { flex: 1, background: "#0f1830", color: "#dfe8ff", border: "1px solid #2a3550", borderRadius: 8, padding: 8 };
const reviewRow = { display: "flex", gap: 8, alignItems: "center", padding: "6px 0" };
const previewImg = { maxWidth: 220, borderRadius: 8, border: "1px solid #2a3550", display: "block", marginBottom: 8 };
const resultImg = { maxWidth: 360, width: "100%", borderRadius: 8, border: "1px solid #2a3550", display: "block" };
const stageBox = { position: "relative", display: "inline-block", maxWidth: 360, marginBottom: 8, border: "1px solid #2a3550", borderRadius: 8, overflow: "hidden" };
const stageImg = { display: "block", width: "100%", userSelect: "none" };
const stageOverlay = { position: "absolute", inset: 0, cursor: "crosshair", touchAction: "none" };
const errText = { color: "#e08a8a", fontSize: 13, marginTop: 6 };
